import axios from "axios";
import { Loader2, Save } from "lucide-react";
import { useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "src/components/ui/button";
import { Card, CardContent } from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { useLanguage } from "src/context/LanguageContext";
import { generateId } from "src/lib/utils";
import EditorTextArea from "../../../../components/EditorTextArea";
import QuizBuilder from "../../../../components/Quiz/quiz-builder";
import { BaseUrlApi } from "../../../../lib/api";
import { QuestionType } from "../../../../lib/types";

export default function AddSurvey() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();

  const [loadingLessons, setLoadingLessons] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { language } = useLanguage?.() || { language: "en" };

  // Form state tailored for surveys only
  const [form, setForm] = useState({
    title: "",
    description: "",
    questions: [
      {
        id: generateId(),
        type: QuestionType.SINGLE_CHOICE,
        text: "",
        options: [
          { id: generateId(), text: "" },
          { id: generateId(), text: "" },
        ],
      },
    ],
  });

  // Update questions for survey format (no scores or correct answers)
  const updateQuestions = useCallback((questions) => {
    const formattedQuestions = questions.map((question) => ({
      text: question.text,
      type: question.type,
      options: question.options.map((option) => ({
        text: option.text,
      })),
    }));
    setForm((prevForm) => ({ ...prevForm, questions: formattedQuestions }));
  }, []);

  // Handle survey submission
  const handleSubmitSurvey = async () => {
    setLoading(true);
    setErrors({});

    const newErrors = {};
    if (!form.title) newErrors.title = ["Title is required"];
    if (form.questions.length === 0)
      newErrors.questions = ["At least one question is required"];

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      toast("Validation Error", {
        description: "Please check the form for errors",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("description", form.description);

      form.questions.forEach((question, i) => {
        formData.append(`questions[${i}][text]`, question.text || "");
        formData.append(`questions[${i}][type]`, question.type || "");
        question.options.forEach((option, j) => {
          formData.append(`questions[${i}][options][${j}]`, option.text);
        });
      });

      await axios.post(`${BaseUrlApi}/surveys`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast("Success", { description: "Survey created successfully" });
      navigate(-1);
    } catch (error) {
      console.error("Error creating survey:", error);
      toast("Error", {
        description: "Failed to create survey. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="w-full sm:w-fit">
          <p className="font-bold text-2xl pt-5" id="category">
            {language === "ar" ? "إضافة استطلاع" : "Add Survey"}
          </p>
          <p className="font-medium text-sm text-muted-foreground">
            {language === "ar" ? "أنشئ استطلاع جديد" : "Create a new survey"}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-fit">
          <Button
            className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl"
            onClick={handleSubmitSurvey}
            disabled={loading}
          >
            {loading ? (
              <div className="animate-spin mr-2">
                <Loader2 className="h-4 w-4" />
              </div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {language === "ar" ? "إرسال الاستطلاع" : "Submit Survey"}
          </Button>
        </div>
      </div>

      {/* General Information Section */}
      <div className="col-span-1 lg:col-span-2">
        <Card className="h-full flex flex-col">
          <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
            <p className="font-bold text-xl border-b pb-2">
              {language === "ar" ? "المعلومات العامة" : "General Information"}
            </p>
            <div className="space-y-4 flex-1 flex flex-col">
              <div className="flex items-center gap-4 flex-col sm:flex-row">
                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="title_en" className="text-sm">
                    {language === "ar" ? "العنوان" : "Title"}
                  </label>
                  <Input
                    readOnly={loading}
                    id="title"
                    name="title"
                    type="text"
                    placeholder={
                      language === "ar"
                        ? "أدخل العنوان"
                        : "Enter title "
                    }
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                  />
                  {errors?.title && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.title[0]}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <label htmlFor="description" className="text-sm">
                  {language === "ar" ? "الوصف" : "Description"}
                </label>
                <EditorTextArea
                  form={form}
                  setForm={setForm}
                  disabled={loading}
                  en="description"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Survey Questions Section */}
      <div className="col-span-1 lg:col-span-2">
        <Card className="h-full flex flex-col">
          <CardContent className="p-4">
            <p className="font-bold text-xl border-b pb-2 mb-4">
              {language === "ar" ? "أسئلة الاستطلاع" : "Survey Questions"}
            </p>
            <QuizBuilder
              initialQuestions={form.questions.map((q) => ({
                id: q.id,
                type: q.type,
                text: q.text || "",
                options: q.options.map((o) => ({
                  id: o.id,
                  text: o.text || "",
                })),
              }))}
              onQuestionsChange={updateQuestions}
              disabled={loading}
              isSurvey={true}
            />
            {errors?.questions && (
              <p className="text-red-500 font-semibold text-sm mt-2">
                {errors.questions[0]}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
